import React, { createContext, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import {
    DATA_TABLE_CHART_REPORT_CLASS,
    REPORT_TABLE_CELL_FONT_STYLE,
    REPORT_TABLE_HEADER_CELL_FONT_STYLE,
    REPORT_TABLE_FONT_CLASS,
    REPORT_TABLE_HEADER_CELL_CLASS,
    REPORT_TABLE_TEXT_CLASS,
    REPORT_TABLE_HEADER_TEXT_CLASS,
    REPORT_TABLE_WRAPPER_CLASS,
} from '../../utils/reportTableTokens';

const variantStyles = {
    default: {
        wrapper: 'overflow-x-auto',
        table: 'w-full text-left text-sm',
        headRow: 'bg-gray-50 border-y border-gray-200',
        headCell: 'px-6 py-4 text-xs font-semibold text-gray-600 uppercase tracking-wider',
        body: 'divide-y divide-gray-100',
        row: 'hover:bg-gray-50 transition-colors',
        cell: 'px-6 py-4 text-sm',
        empty: 'px-6 py-8 text-sm text-gray-500 text-center',
    },
    compact: {
        wrapper: 'overflow-hidden rounded-xl border border-gray-200 bg-white',
        table: 'min-w-full text-sm',
        headRow: 'bg-gray-50 text-gray-600',
        headCell: 'px-4 py-3 text-left font-medium text-xs',
        body: 'divide-y divide-gray-100',
        row: '',
        cell: 'px-4 py-3 text-sm',
        empty: 'px-4 py-6 text-sm text-gray-500',
    },
    spacious: {
        wrapper: 'overflow-x-auto',
        table: 'w-full text-left border-collapse text-sm',
        headRow: 'bg-gray-50/50 border-y border-gray-100',
        headCell: 'px-6 py-5 text-xs font-black text-gray-400 uppercase tracking-widest',
        body: 'divide-y divide-gray-50',
        row: 'hover:bg-gray-50/30 transition-colors',
        cell: 'px-6 py-6 text-sm',
        empty: 'px-6 py-10 text-sm text-gray-500 text-center',
    },
    report: {
        wrapper: `${DATA_TABLE_CHART_REPORT_CLASS} ${REPORT_TABLE_WRAPPER_CLASS} ${REPORT_TABLE_FONT_CLASS} ${REPORT_TABLE_TEXT_CLASS} overflow-x-auto bg-white`,
        table: `table-fixed ${REPORT_TABLE_FONT_CLASS} ${REPORT_TABLE_TEXT_CLASS}`,
        headRow: '',
        headCell: REPORT_TABLE_HEADER_TEXT_CLASS,
        body: '',
        row: '',
        cell: REPORT_TABLE_TEXT_CLASS,
        empty: `report-table-empty ${REPORT_TABLE_TEXT_CLASS}`,
    },
};

const TableContext = createContext({ variant: 'default', styles: variantStyles.default });

const alignClass = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
};

export const Table = ({
    children,
    variant = 'default',
    className = '',
    wrapperClassName = '',
}) => {
    const styles = variantStyles[variant] || variantStyles.default;

    const reportFontStyle = variant === 'report' ? REPORT_TABLE_CELL_FONT_STYLE : undefined;

    return (
        <TableContext.Provider value={{ variant, styles }}>
            <div
                className={`${styles.wrapper} ${wrapperClassName}`}
                style={reportFontStyle}
            >
                <table className={`${styles.table} ${className}`.trim()} style={reportFontStyle}>
                    {children}
                </table>
            </div>
        </TableContext.Provider>
    );
};

const useTableStyles = () => useContext(TableContext).styles;

export const TableHead = ({ children, className = '' }) => (
    <thead className={className}>{children}</thead>
);

export const TableBody = ({ children, className = '' }) => {
    const styles = useTableStyles();
    return <tbody className={`${styles.body} ${className}`.trim()}>{children}</tbody>;
};

export const TableRow = ({ children, className = '', hover = true }) => {
    const styles = useTableStyles();
    return (
        <tr className={`${hover ? styles.row : ''} ${className}`.trim()}>
            {children}
        </tr>
    );
};

export const TableHeadCell = ({ children, align = 'left', className = '' }) => {
    const { variant, styles } = useContext(TableContext);
    const isReport = variant === 'report';
    const reportHeaderClass = isReport ? REPORT_TABLE_HEADER_CELL_CLASS : '';
    return (
        <th
            className={`${styles.headCell} ${alignClass[align] || alignClass.left} ${reportHeaderClass} ${className}`.trim()}
            style={isReport ? REPORT_TABLE_HEADER_CELL_FONT_STYLE : undefined}
        >
            {children}
        </th>
    );
};

export const TableCell = ({ children, align = 'left', colSpan = undefined, className = '' }) => {
    const { variant, styles } = useContext(TableContext);
    const isReport = variant === 'report';
    return (
        <td
            colSpan={colSpan}
            className={`${styles.cell} ${alignClass[align] || alignClass.left} ${className}`.trim()}
            style={isReport ? REPORT_TABLE_CELL_FONT_STYLE : undefined}
        >
            {children}
        </td>
    );
};

export const TableHeaderRow = ({ children, className = '' }) => {
    const styles = useTableStyles();
    return <tr className={`${styles.headRow} ${className}`.trim()}>{children}</tr>;
};

export const TableEmpty = ({ colSpan, children }) => {
    const { t } = useTranslation('common');
    const { variant, styles } = useContext(TableContext);
    const isReport = variant === 'report';
    return (
        <TableRow hover={false}>
            <td
                colSpan={colSpan}
                className={styles.empty}
                style={isReport ? REPORT_TABLE_CELL_FONT_STYLE : undefined}
            >
                {children ?? t('noData')}
            </td>
        </TableRow>
    );
};
